import { makeStyles } from '@fluentui/react-components';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { TOAST_PORTAL_CONTAINER_ID } from '../../constants/app';
import { PropsWithChildren } from '../../types/props-with-children';

interface PortalContainerProviderProps extends PropsWithChildren {
    containerId?: string;
}

// Context to provide the portal container
const PortalContainerContext = React.createContext<HTMLElement | null>(null);

/**
 * Styles
 */
const usePortalContainerStyles = makeStyles({
    root: {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1000,
    },
});

/**
 * End Styles
 */

export const usePortalContainer = (): HTMLElement | null => {
    return React.useContext(PortalContainerContext);
};

export const PortalContainerProvider: React.FC<PortalContainerProviderProps> = (props) => {
    const { children, containerId = TOAST_PORTAL_CONTAINER_ID } = props;
    const [container, setContainer] = React.useState<HTMLElement | null>(null);

    // Style hooks
    const portalContainerStyles = usePortalContainerStyles();

    React.useEffect(() => {
        // Create or find the portal container
        // We need to manually create DOM elements here because:
        // 1. Toasts need to render outside the normal React component tree to avoid z-index/positioning issues
        // 2. Unlike panels (which use Fluent UI's OverlayDrawer with built-in container management),
        //    there's no Fluent UI toast component that automatically handles portal containers
        // 3. The container needs specific positioning (fixed, full-screen overlay) and pointer events
        //    configuration to ensure toasts appear above all content but don't block interactions
        // 4. React portals require a real DOM element as the target, which must exist before rendering
        let portalContainer = document.getElementById(containerId);

        if (!portalContainer) {
            portalContainer = document.createElement('div');
            portalContainer.id = containerId;
            portalContainer.className = portalContainerStyles.root;
            document.body.appendChild(portalContainer);
        }

        setContainer(portalContainer);

        return () => {
            // Cleanup: remove the container if it's empty
            if (portalContainer && portalContainer.children.length === 0 && portalContainer.parentNode) {
                portalContainer.parentNode.removeChild(portalContainer);
            }
        };
    }, [containerId, portalContainerStyles.root]);

    return <PortalContainerContext.Provider value={container}>{children}</PortalContainerContext.Provider>;
};

// Hook to create a portal
export const usePortal = (children: React.ReactNode): React.ReactPortal | null => {
    const container = usePortalContainer();

    if (!container || !children) {
        return null;
    }

    return createPortal(children, container);
};
